import { Attribute, useEditor, UseEditorOptions } from "@tiptap/react";

import {
    FontSize,
    HeaderImage,
    LineHeight,
    PageBreak,
    PageHeader,
    PageNode,
    PaginationExtension,
} from "./extensions";

import FontFamily from "@tiptap/extension-font-family";
import Heading from "@tiptap/extension-heading";
import Highlight from "@tiptap/extension-highlight";
import { Link } from "@tiptap/extension-link";
import SubScript from "@tiptap/extension-subscript";
import Paragraph from "@tiptap/extension-paragraph";
import Superscript from "@tiptap/extension-superscript";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Underline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import { ResizableImage } from "tiptap-extension-resizable-image";
import { toKebabCase } from "../../utils";
import { ContentEditable } from "./extensions/ContentEditable";
import { EditabilityFilter } from "./extensions/EditabilityFilter";

type Props = UseEditorOptions & {
    headerLogo?: string;
    options?: {
        defaultFontSize?: string;
    };
};

const createCustomAttributes = (
    data: Record<string, Partial<Attribute & { withoutStyle?: boolean }> | true>
) => {
    for (const [key, value] of Object.entries(data)) {
        const kebabCaseKey = toKebabCase(key);
        const htmlAttributeKey = `data-${kebabCaseKey}`;
        const defaultConfig: Attribute = {
            default: null,
            parseHTML: (element) => element.getAttribute(htmlAttributeKey),
            renderHTML: (attributes) => {
                const value =
                    attributes[key] ||
                    (data[key] !== true
                        ? (data[key] as Partial<Attribute>).default
                        : undefined);
                return {
                    [htmlAttributeKey]: value,
                    ...(value !== undefined && value !== null
                        ? { style: `${kebabCaseKey}: ${value}` }
                        : {}),
                };
            },
        };
        data[key] =
            value === true
                ? defaultConfig
                : {
                      ...defaultConfig,
                      ...value,
                  };
    }
    return data;
};

const CustomHeading = Heading.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            ...createCustomAttributes({
                paddingTop: true,
                paddingBottom: true,
            }),
        };
    },
    renderHTML({ node, HTMLAttributes }) {
        const level = this.options.levels.includes(node.attrs.level)
            ? node.attrs.level
            : this.options.levels[0];

        // Define default styles for each heading level
        const defaultStyles: Record<number, string> = {
            1: 'font-size: 16pt !important; font-weight: bold;',
            2: 'font-size: 14pt !important; font-weight: bold;',
            3: 'font-size: 12pt !important; font-weight: bold;',
        };

        const existingStyle = HTMLAttributes.style || '';
        const defaultStyle = defaultStyles[level] || '';

        // Merge existing styles with default styles (existing takes precedence)
        const style = existingStyle
            ? `${defaultStyle} ${existingStyle}`
            : defaultStyle;

        return [
            `h${level}`,
            {
                ...HTMLAttributes,
                style,
            },
            0,
        ];
    },
});
const CustomParagraph = Paragraph.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            ...createCustomAttributes({
                paddingTop: true,
                paddingBottom: true,
                border: true,
                removeBorderOnPrint: { withoutStyle: true },
            }),
        };
    },
});
const CustomTable = Table.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            ...createCustomAttributes({
                backgroundColor: true,
                borderCollapse: true,
                borderSpacing: true,
                border: true,
                width: true,
            }),
        };
    },
});
CustomTable.configure({
    resizable: true,
});
const CustomTableRow = TableRow.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            ...createCustomAttributes({
                backgroundColor: true,
                color: true,
            }),
        };
    },
});

const CustomTableCell = TableCell.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            ...createCustomAttributes({
                backgroundColor: true,
                width: true,
                color: true,
                border: true,
                removeBorderOnPrint: { withoutStyle: true },
            }),
        };
    },
});
const CustomTableHeader = TableHeader.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            ...createCustomAttributes({
                backgroundColor: true,
                width: true,
                color: true,
                border: true,
                removeBorderOnPrint: { withoutStyle: true },
            }),
        };
    },
});

function useMyEditor({ headerLogo, options, ...props }: Props) {
    const editor = useEditor({
        extensions: [
            PageHeader,
            HeaderImage.configure({ src: headerLogo }),
            FontSize.configure({
                defaultFontSize: options?.defaultFontSize || "10pt",
            }),
            PaginationExtension,
            PageNode.configure({
                headerImageSrc: headerLogo || "",
            }),
            PageBreak,
            StarterKit.configure({
                // heading: false,
                paragraph: false,
            }),
            CustomHeading.configure({
                levels: [1, 2, 3],
            }),
            CustomParagraph,
            Underline,
            Link.configure({
                openOnClick: true,
            }),
            Superscript,
            SubScript,
            Highlight,
            TextAlign.configure({ types: ["heading", "paragraph"] }),
            CustomTable,
            CustomTableRow,
            CustomTableHeader,
            CustomTableCell,
            FontFamily,
            TextStyle,
            Color,
            LineHeight,
            ResizableImage.configure({
                allowBase64: true,
                defaultWidth: 200,
                defaultHeight: 200,
                async onUpload(file: File) {
                    const reader = new FileReader();
                    const src = await new Promise<string>((resolve) => {
                        reader.onload = () => resolve(reader.result as string);
                        reader.readAsDataURL(file);
                    });
                    return {
                        src,
                        "data-keep-ratio": true,
                    };
                },
            }),
            ContentEditable,
            EditabilityFilter,
        ],
        ...props,
    });

    return editor;
}

export default useMyEditor;
